/**
 * Authenticated GraphQL client used by learner-app actions.
 * Read queries are persisted to IndexedDB so they can be replayed offline.
 * Mutations and write-like queries are queued and flushed when the network returns.
 */
import { getGqlCache, isQueueableDocument, putGqlCache, queuedMutationFallback } from "offline/cache";
import { postGql } from "offline/gqlClient";
import { isNetworkError, isOnline } from "offline/network";
import { enqueueGql } from "offline/queue";
import { shouldPreferSession } from "offline/session";

/**
 * Posts a GraphQL query with the stored JWT and optional loading dispatch.
 *
 * @param {Function} dispatch App store dispatch.
 * @param {string} query GraphQL document.
 * @param {Object} [variable={}] Query variables.
 * @param {string} [loadhook=""] Optional loading action type.
 * @returns {Promise<*>} Unwrapped GraphQL field value, cache replay, or queue fallback.
 */
const asterAxios = (dispatch, query, variable = {}, loadhook = "") => {
  loadhook && dispatch({ type: loadhook, value: false });
  return new Promise((resolve, reject) => {
    /**
     * Stores a write and resolves so optimistic UI can continue.
     *
     * @returns {Promise<void>}
     */
    const queueWrite = async () => {
      try {
        await enqueueGql(query, variable);
        resolve(queuedMutationFallback(query));
      } catch (queueErr) {
        reject(queueErr);
      }
    };

    (async () => {
      if (!isOnline()) {
        if (isQueueableDocument(query)) {
          await queueWrite();
          return;
        }
        if (!shouldPreferSession()) {
          const cached = await getGqlCache(query, variable);
          if (cached !== undefined) {
            resolve(cached);
            return;
          }
        }
        reject({ message: "Network Error", code: "ERR_NETWORK" });
        return;
      }

      try {
        const value = await postGql(query, variable);
        putGqlCache(query, variable, value);
        resolve(value);
      } catch (err) {
        if (isNetworkError(err)) {
          if (isQueueableDocument(query)) {
            await queueWrite();
            return;
          }
          if (!shouldPreferSession()) {
            const cached = await getGqlCache(query, variable);
            if (cached !== undefined) {
              resolve(cached);
              return;
            }
          }
        }
        if (process.env.NODE_ENV !== "production") console.log("[Api Error]", err);
        reject(err);
      }
    })().finally(() => {
      loadhook && dispatch({ type: loadhook, value: true });
    });
  });
};

export default asterAxios;
